import { assertOk } from "./errors.js";
import { normalizeApiUrl } from "./url.js";
import type {
  DeviceTokenRecord,
  ListMessagesParams,
  ListRoomsParams,
  Message,
  MessagesResponse,
  RegisterDeviceTokenParams,
  RoomsResponse,
  SendMessageParams,
  ToggleReactionParams,
  UpdateMessageParams,
  StartCallParams,
  StartCallResult,
  CallRecord,
  LiveKitCredentials,
} from "./types.js";

export class RestClient {
  readonly apiUrl: string;
  private sessionToken: string | null = null;

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  setSessionToken(token: string | null): void {
    this.sessionToken = token;
  }

  private headers(extra?: HeadersInit): HeadersInit {
    const base: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.sessionToken) {
      base.Authorization = `Bearer ${this.sessionToken}`;
    }
    return { ...base, ...(extra as Record<string, string> | undefined) };
  }

  private path(suffix: string): string {
    const normalized = suffix.startsWith("/") ? suffix : `/${suffix}`;
    return `${normalizeApiUrl(this.apiUrl)}${normalized}`;
  }

  async fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(this.path(path), {
      ...init,
      headers: this.headers(init?.headers),
    });
    await assertOk(response, path);
    return response.json() as Promise<T>;
  }

  async listRooms(params: ListRoomsParams = {}): Promise<RoomsResponse> {
    const search = new URLSearchParams();
    if (params.participantId) search.set("participantId", params.participantId);
    if (params.externalId) search.set("externalId", params.externalId);
    if (params.customTypesFilter?.length) {
      search.set("customTypesFilter", params.customTypesFilter.join(","));
    }
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.cursor) search.set("cursor", params.cursor);
    const qs = search.toString();
    return this.fetchJson<RoomsResponse>(`/chat/rooms${qs ? `?${qs}` : ""}`);
  }

  async listMessages(roomId: string, params: ListMessagesParams = {}): Promise<MessagesResponse> {
    const search = new URLSearchParams();
    if (params.viewerId) search.set("viewerId", params.viewerId);
    if (params.cursor) search.set("cursor", params.cursor);
    if (params.limit != null) search.set("limit", String(params.limit));
    const qs = search.toString();
    return this.fetchJson<MessagesResponse>(
      `/chat/rooms/${encodeURIComponent(roomId)}/messages${qs ? `?${qs}` : ""}`,
    );
  }

  async sendMessage(roomId: string, body: SendMessageParams): Promise<Message> {
    return this.fetchJson<Message>(`/chat/rooms/${encodeURIComponent(roomId)}/messages`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async updateMessage(
    roomId: string,
    messageId: string,
    body: UpdateMessageParams,
  ): Promise<Message> {
    return this.fetchJson<Message>(
      `/chat/rooms/${encodeURIComponent(roomId)}/messages/${encodeURIComponent(messageId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  }

  async deleteMessage(
    roomId: string,
    messageId: string,
    userId?: string,
  ): Promise<Message> {
    const search = userId ? `?userId=${encodeURIComponent(userId)}` : "";
    return this.fetchJson<Message>(
      `/chat/rooms/${encodeURIComponent(roomId)}/messages/${encodeURIComponent(messageId)}${search}`,
      { method: "DELETE" },
    );
  }

  async toggleReaction(
    roomId: string,
    messageId: string,
    body: ToggleReactionParams,
  ): Promise<Message> {
    return this.fetchJson<Message>(
      `/chat/rooms/${encodeURIComponent(roomId)}/messages/${encodeURIComponent(messageId)}/reactions`,
      { method: "POST", body: JSON.stringify(body) },
    );
  }

  async markRoomRead(
    roomId: string,
    userId: string,
    lastReadMessageId?: string,
  ): Promise<{ ok: true; lastReadAt: string; lastReadMessageId: string | null }> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/read`, {
      method: "POST",
      body: JSON.stringify({ userId, lastReadMessageId }),
    });
  }

  async markRoomDelivered(
    roomId: string,
    userId: string,
    lastDeliveredMessageId?: string,
  ): Promise<{ ok: true; lastDeliveredAt: string; lastDeliveredMessageId: string | null }> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/delivery`, {
      method: "POST",
      body: JSON.stringify({ userId, lastDeliveredMessageId }),
    });
  }

  /** Register push token for the connected user (session JWT). */
  async registerDeviceToken(params: RegisterDeviceTokenParams): Promise<DeviceTokenRecord> {
    return this.fetchJson<DeviceTokenRecord>("/users/me/device-tokens", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async unregisterDeviceToken(token: string): Promise<void> {
    const response = await fetch(this.path(`/users/me/device-tokens/${encodeURIComponent(token)}`), {
      method: "DELETE",
      headers: this.headers(),
    });
    await assertOk(response, "unregister device token");
  }

  async getActiveCall(roomId: string): Promise<{ call: CallRecord }> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/calls/active`);
  }

  async startCall(roomId: string, params: StartCallParams): Promise<StartCallResult> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/calls`, {
      method: "POST",
      body: JSON.stringify(params),
    });
  }

  async acceptCall(roomId: string, callId: string): Promise<StartCallResult> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/calls/${encodeURIComponent(callId)}/accept`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async declineCall(roomId: string, callId: string): Promise<CallRecord> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/calls/${encodeURIComponent(callId)}/decline`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async endCall(
    roomId: string,
    callId: string,
    options?: { reason?: "cancelled" | "no_answer" },
  ): Promise<CallRecord> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/calls/${encodeURIComponent(callId)}/end`, {
      method: "POST",
      body: JSON.stringify(options?.reason ? { reason: options.reason } : {}),
    });
  }

  async leaveCall(roomId: string, callId: string): Promise<CallRecord> {
    return this.fetchJson(`/chat/rooms/${encodeURIComponent(roomId)}/calls/${encodeURIComponent(callId)}/leave`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async refreshCallToken(roomId: string, callId: string): Promise<{ livekit: LiveKitCredentials }> {
    return this.fetchJson(
      `/chat/rooms/${encodeURIComponent(roomId)}/calls/${encodeURIComponent(callId)}/token`,
      { method: "POST", body: JSON.stringify({}) },
    );
  }
}
